<template>
  <div class="result" v-if="result">
    <div class="result-inner">
      <div class="result-header">
        <div class="result-badge">Assessment Complete</div>
        <h1>{{ result.result_title }}</h1>
        <div class="score-display">
          <div class="score-circle">
            <span class="score-num">{{ result.total_score }}</span>
            <span class="score-label">Total Score</span>
          </div>
          <div class="score-meta">
            <p class="quiz-name-ref">{{ result.quiz_name }}</p>
            <p v-if="result.percentile != null">Estimated percentile: {{ result.percentile }}%</p>
            <p>Score range: {{ result.score_range.min }} – {{ result.score_range.max }}</p>
            <p>Answered: {{ result.answered_count }} questions</p>
          </div>
        </div>
      </div>

      <!-- Dynamic radar -->
      <div class="radar-section" v-if="radarValues.length">
        <h2>Multi-Dimensional Radar Profile</h2>
        <p class="radar-sub">Based on your actual response pattern across {{ radarValues.length }} constructs</p>
        <div class="radar-wrap">
          <svg viewBox="0 0 300 300" class="radar-svg">
            <polygon
              v-for="(ring, i) in 5"
              :key="'ring'+i"
              :points="hexPoints(130 - i * 26)"
              fill="none"
              stroke="#dce4f0"
              stroke-width="1"
            />
            <line
              v-for="(_, i) in radarValues.length"
              :key="'line'+i"
              x1="150" y1="150"
              :x2="150 + 130 * Math.cos(axisAngle(i))"
              :y2="150 + 130 * Math.sin(axisAngle(i))"
              stroke="#dce4f0"
              stroke-width="1"
            />
            <polygon
              :points="dataPoints"
              fill="rgba(26,58,107,0.22)"
              stroke="#1a3a6b"
              stroke-width="2"
            />
            <circle
              v-for="(pt, i) in pointCoords"
              :key="'pt'+i"
              :cx="pt.x"
              :cy="pt.y"
              r="4"
              fill="#e8c547"
            />
            <text
              v-for="(v, i) in radarValues"
              :key="'lbl'+i"
              :x="150 + 148 * Math.cos(axisAngle(i))"
              :y="150 + 148 * Math.sin(axisAngle(i))"
              text-anchor="middle"
              dominant-baseline="middle"
              font-size="8"
              fill="#6b7fa0"
            >{{ shortLabel(v.label) }}</text>
          </svg>
        </div>
        <div class="dim-bars">
          <div v-for="v in radarValues" :key="v.label" class="dim-row">
            <span class="dim-name">{{ v.label }}</span>
            <div class="dim-track">
              <div class="dim-fill" :style="{ width: v.percentile + '%' }" />
            </div>
            <span class="dim-pct">{{ v.percentile }}%</span>
          </div>
        </div>
      </div>

      <!-- Detailed report -->
      <div class="report-section">
        <h2>In-Depth Analysis Report</h2>
        <div class="report-body">
          <p
            v-for="(para, i) in reportParagraphs"
            :key="i"
            class="report-para"
            :class="{ 'report-heading': isHeading(para) }"
          >{{ para }}</p>
        </div>
      </div>

      <div class="result-actions">
        <button class="btn-secondary" @click="goHome">Back to Home</button>
        <button class="btn-primary" @click="retake">Retake Test</button>
      </div>
    </div>
  </div>
</template>

<script>
import { quizStore } from '../store/quiz'

export default {
  name: 'ResultPage',
  props: {
    result: { type: Object, default: null }
  },
  computed: {
    radarValues () {
      if (!this.result || !this.result.radar || !this.result.radar.values) return []
      return this.result.radar.values
    },
    reportParagraphs () {
      if (!this.result || !this.result.detailed_report) return []
      return this.result.detailed_report.split('\n\n').filter(p => p.trim())
    },
    pointCoords () {
      const maxR = 130
      return this.radarValues.map((v, i) => {
        const r = maxR * (v.percentile / 100)
        return {
          x: 150 + r * Math.cos(this.axisAngle(i)),
          y: 150 + r * Math.sin(this.axisAngle(i))
        }
      })
    },
    dataPoints () {
      return this.pointCoords.map(p => `${p.x},${p.y}`).join(' ')
    }
  },
  methods: {
    axisAngle (i) {
      const n = this.radarValues.length || 6
      return (i * (360 / n) - 90) * Math.PI / 180
    },
    shortLabel (label) {
      return label.length > 14 ? label.slice(0, 12) + '…' : label
    },
    isHeading (para) {
      return /^(I{1,3}|IV|V|VI{0,3}|IX|X)\.\s/.test(para) || para.startsWith('[')
    },
    hexPoints (radius) {
      const n = this.radarValues.length || 6
      return Array.from({ length: n }, (_, i) => {
        const angle = this.axisAngle(i)
        return `${150 + radius * Math.cos(angle)},${150 + radius * Math.sin(angle)}`
      }).join(' ')
    },
    goHome () {
      quizStore.resetSession()
      if (this.$route.name === 'Home') return
      this.$router.push({ name: 'Home' })
    },
    retake () {
      const id = quizStore.getState().quizId
      quizStore.resetForRetake()
      this.$router.push({ name: 'QuizTest', params: { id: String(id) } })
    }
  }
}
</script>

<style scoped>
.result {
  background: linear-gradient(180deg, #f0f4fa 0%, #fff 100%);
  padding: 2.5rem 0 4rem;
  min-height: 80vh;
}

.result-inner {
  max-width: 860px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.result-header {
  background: linear-gradient(135deg, #0d1f3c, #1a3a6b);
  border-radius: 20px;
  padding: 2.5rem;
  color: #fff;
  margin-bottom: 2rem;
  text-align: center;
}

.result-badge {
  display: inline-block;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #e8c547;
  border: 1px solid rgba(232,197,71,0.4);
  padding: 0.3rem 0.85rem;
  border-radius: 20px;
  margin-bottom: 1rem;
}

.result-header h1 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.3rem, 3vw, 1.8rem);
  margin-bottom: 2rem;
  line-height: 1.3;
}

.score-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
}

.score-circle {
  width: 120px;
  height: 120px;
  border: 3px solid #e8c547;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(232,197,71,0.1);
}

.score-num {
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #e8c547;
  line-height: 1;
}

.score-label {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.6);
  margin-top: 0.25rem;
}

.score-meta { text-align: left; }

.quiz-name-ref {
  font-size: 0.9rem;
  color: rgba(255,255,255,0.8);
  margin-bottom: 0.5rem;
}

.score-meta p {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.55);
  margin-bottom: 0.25rem;
}

.radar-section,
.report-section {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 16px rgba(13,31,60,0.06);
}

.radar-section h2,
.report-section h2 {
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem;
  color: #0d1f3c;
  margin-bottom: 0.35rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e8c547;
  display: inline-block;
}

.radar-sub {
  font-size: 0.82rem;
  color: #6b7fa0;
  margin-bottom: 1.25rem;
}

.radar-wrap {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 1.25rem;
}

.radar-svg { width: 300px; height: 300px; }

.dim-bars { margin-top: 0.5rem; }

.dim-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.55rem;
}

.dim-name {
  width: 130px;
  font-size: 0.78rem;
  color: #4a6080;
  flex-shrink: 0;
}

.dim-track {
  flex: 1;
  height: 8px;
  background: #e8edf5;
  border-radius: 4px;
  overflow: hidden;
}

.dim-fill {
  height: 100%;
  background: linear-gradient(90deg, #1a3a6b, #c9a227);
  border-radius: 4px;
  transition: width 0.6s ease;
}

.dim-pct {
  width: 36px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a3a6b;
  text-align: right;
}

.report-body { margin-top: 0.5rem; }

.report-para {
  font-family: 'Source Serif 4', serif;
  font-size: 0.95rem;
  color: #2a4060;
  line-height: 1.9;
  margin-bottom: 1.25rem;
  text-align: justify;
}

.report-heading {
  font-family: 'Playfair Display', serif;
  font-weight: 600;
  color: #0d1f3c;
  font-size: 1rem;
  margin-top: 0.5rem;
}

.result-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.85rem 2rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #1a3a6b, #0d1f3c);
  color: #fff;
}

.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(26,58,107,0.3); }

.btn-secondary {
  background: #fff;
  color: #1a3a6b;
  border: 1.5px solid #1a3a6b;
}

.btn-secondary:hover { background: #f0f4fa; }

@media (max-width: 640px) {
  .score-display { flex-direction: column; }
  .dim-name { width: 90px; font-size: 0.72rem; }
}
</style>
